// Livestock: the Godot build's cows, bulls and bison (scripts/animals/), all on the Styloo cow rig
// for now exactly as in Godot (Nick's call there: bull and bison are the cow model fitted larger).
// Bulls get a darker hide and horns' worth of bulk; bison a shaggy brown and a heavy front.
//
// The sim (sim/critters.ts) runs them on the server; this view eases each one toward its latest
// networked pose and picks the baked clip from its state and speed: graze → iddle, walk → walk,
// flee / charge → run (charge faster). A dead one tips over, lies a while, then sinks away.

import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { clone as skClone } from 'three/examples/jsm/utils/SkeletonUtils.js';
import { CState, CType } from '../../../sim/critters';
import type { CritterNet } from '../../../sim/runwire';
import { lerpAngle } from '../../../sim/core';
import { withHaze } from '../materials';

const BASE = (import.meta as unknown as { env?: { BASE_URL?: string } }).env?.BASE_URL ?? '/';
const MODEL = BASE + 'assets/run/cow.glb';

// footprint per type (Godot body_length), and a hide tint multiplied into the cow's texture
const LEN = [2.1, 2.45, 2.6];
const BULK = [1, 1.08, 1.16]; // extra height / girth over the fitted length
const TINT = [new THREE.Color(0.86, 0.84, 0.82), new THREE.Color(0.52, 0.4, 0.36), new THREE.Color(0.5, 0.36, 0.26)];
const MODEL_SPAN = 3.344; // the cow mesh's longest horizontal extent (nose to tail)

interface Beast {
  id: number; type: number;
  root: THREE.Group; model: THREE.Object3D;
  mixer: THREE.AnimationMixer;
  acts: Record<string, THREE.AnimationAction>;
  cur: string;
  x: number; y: number; z: number; yaw: number;
  tx: number; ty: number; tz: number; tyaw: number;
  spd: number; st: number;
  deadAt: number; seen: number;
}

export class CritterView {
  readonly group = new THREE.Group();
  private src: { scene: THREE.Object3D; clips: THREE.AnimationClip[] } | null = null;
  private mats: THREE.Material[][] = [[], [], []];
  private beasts = new Map<number, Beast>();
  private frame = 0;
  private disposables: { dispose(): void }[] = [];
  /** handed a hidden herd to compile once the model is in (client/render/warm.ts) */
  prewarm: ((g: THREE.Group) => void) | null = null;

  constructor(scene: THREE.Scene) {
    this.group.name = 'critters';
    scene.add(this.group);
    new GLTFLoader().load(MODEL, (g) => {
      this.src = { scene: g.scene, clips: g.animations };
      // one hazed toon-ish material per type (the texture shared), so three herds cost three programs at most
      g.scene.traverse((o) => {
        const m = o as THREE.SkinnedMesh;
        if (!m.isMesh) return;
        const base = m.material as THREE.MeshStandardMaterial;
        for (let t = 0; t < 3; t++) {
          const mt = base.clone();
          mt.color.copy(TINT[t]);
          mt.roughness = 0.95; mt.metalness = 0;
          withHaze(mt, 0.5);
          this.mats[t].push(mt);
          this.disposables.push(mt);
        }
        if (base.map) this.disposables.push(base.map);
      });
      // one of each herd, never shown: their shaders compile ahead of the first cow on an island
      if (this.prewarm) {
        const g = new THREE.Group();
        for (let t = 0; t < 3; t++) { const b = this.make({ id: t, type: t, x: 0, y: 0, z: 0, yaw: 0, st: 0 } as CritterNet); if (b) { b.root.removeFromParent(); g.add(b.root); } }
        this.prewarm(g);
      }
    }, undefined, (e) => console.warn('[critters] model failed', e));
  }

  private make(c: CritterNet): Beast | null {
    if (!this.src) return null;
    const model = skClone(this.src.scene);
    const t = c.type as CType;
    const s = LEN[t] / MODEL_SPAN;
    model.scale.set(s * BULK[t], s * BULK[t], s);
    let mi = 0;
    model.traverse((o) => {
      const m = o as THREE.SkinnedMesh;
      if (!m.isMesh) return;
      m.material = this.mats[t][Math.min(mi++, this.mats[t].length - 1)];
      m.castShadow = true; m.receiveShadow = false;
      m.frustumCulled = false; // skinned bounds lag the pose
    });
    const root = new THREE.Group();
    root.add(model);
    this.group.add(root);
    const mixer = new THREE.AnimationMixer(model);
    const acts: Record<string, THREE.AnimationAction> = {};
    for (const clip of this.src.clips) {
      if (clip.name === 'walking' || clip.name === 'jump') continue;
      const a = mixer.clipAction(clip);
      a.setLoop(THREE.LoopRepeat, Infinity);
      acts[clip.name] = a;
    }
    const b: Beast = {
      id: c.id, type: t, root, model, mixer, acts, cur: '',
      x: c.x, y: c.y, z: c.z, yaw: c.yaw, tx: c.x, ty: c.y, tz: c.z, tyaw: c.yaw, spd: 0, st: c.st, deadAt: -1, seen: 0,
    };
    // desync the herd
    this.play(b, 'iddle', 0);
    mixer.setTime(((c.id * 0.6180339) % 1) * 3);
    return b;
  }

  private play(b: Beast, name: string, fade = 0.25) {
    if (b.cur === name) return;
    const next = b.acts[name];
    if (!next) return;
    const prev = b.acts[b.cur];
    next.reset().play();
    if (prev && fade > 0) next.crossFadeFrom(prev, fade, false);
    else if (prev) prev.stop();
    b.cur = name;
  }

  /** latest networked herd (the run block); called every frame */
  sync(list: readonly CritterNet[], dt: number, time: number, ground: (x: number, z: number, y: number) => number) {
    this.frame++;
    for (const c of list) {
      let b = this.beasts.get(c.id);
      if (b && b.type !== c.type) { this.drop(b); b = undefined; }
      if (!b) { const nb = this.make(c); if (!nb) continue; b = nb; this.beasts.set(c.id, b); }
      b.seen = this.frame;
      b.tx = c.x; b.ty = c.y; b.tz = c.z; b.tyaw = c.yaw;
      if (c.st === CState.Dead && b.st !== CState.Dead) b.deadAt = time;
      b.st = c.st;
    }
    for (const b of this.beasts.values()) {
      if (b.seen !== this.frame) { this.drop(b); continue; }
      // ease toward the networked pose (snapshots are 30 Hz; ~70 ms to settle)
      const k = 1 - Math.exp(-dt * 14);
      const px = b.x, pz = b.z;
      if (Math.hypot(b.tx - b.x, b.tz - b.z) > 6) { b.x = b.tx; b.z = b.tz; b.y = b.ty; }
      b.x += (b.tx - b.x) * k; b.y += (b.ty - b.y) * k; b.z += (b.tz - b.z) * k;
      b.yaw = lerpAngle(b.yaw, b.tyaw, 1 - Math.exp(-dt * 10));
      const v = dt > 0 ? Math.hypot(b.x - px, b.z - pz) / dt : 0;
      b.spd += (v - b.spd) * Math.min(1, dt * 6);
      b.root.position.set(b.x, b.y, b.z);
      b.root.rotation.set(0, b.yaw, 0);
      if (b.st === CState.Dead) {
        // tip over onto its side, lie a while, then sink into the grass
        const age = time - b.deadAt;
        const f = Math.min(1, age / 0.55);
        b.root.rotation.z = (1 - (1 - f) * (1 - f)) * Math.PI / 2 * (b.id & 1 ? 1 : -1);
        b.root.position.y = b.y + 0.35 * f * LEN[b.type] / 2.1 - Math.max(0, age - 8) * 0.25;
        for (const a of Object.values(b.acts)) a.paused = true;
        b.mixer.update(0);
        continue;
      }
      let clip = 'iddle', rate = 1;
      if (b.st === CState.Flee || b.st === CState.Charge || b.spd > 4) { clip = 'run'; rate = Math.max(0.8, Math.min(1.6, b.spd / 7)) * (b.st === CState.Charge ? 1.15 : 1); }
      else if (b.st === CState.Walk || b.spd > 0.5) { clip = 'walk'; rate = Math.max(0.6, Math.min(1.4, b.spd / 1.5)); }
      else if (b.st === CState.Gore) { clip = 'run'; rate = 1.8; }
      this.play(b, clip);
      b.mixer.timeScale = rate;
      b.mixer.update(dt);
    }
    void ground;
  }

  private drop(b: Beast) {
    this.group.remove(b.root);
    b.mixer.stopAllAction();
    this.beasts.delete(b.id);
  }

  clear() { for (const b of [...this.beasts.values()]) this.drop(b); }

  dispose() {
    this.clear();
    this.group.parent?.remove(this.group);
    for (const d of this.disposables) d.dispose();
    this.src?.scene.traverse((o) => { const m = o as THREE.Mesh; if (m.isMesh) m.geometry.dispose(); });
  }
}
